import React, { useState, useRef, useEffect } from "react";
import { Search, Filter, Heart } from "lucide-react";
import "./adminCalendar.css";
import { supabase } from "../../supabase_client.jsx";

//convert date into YYYY-MM-DD
function formatDateKey(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function formatDisplayDate(isoDate) {
  // Split manually to avoid UTC->local timezone shift
  const [year, month, day] = String(isoDate).split("-").map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

// current user formulation 
export default function AdminCalendar({ currentUser, onViewSelect, onAddEvent }) {
 
  const [events, setEvents] = useState([]);
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(today);
  const [view, setView] = useState("month"); //default veiw
  const [isViewMenuOpen, setIsViewMenuOpen] = useState(false);

  //month logic 
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const monthNames = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];

  const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();


  useEffect(() => {
    const fetchEvents = async () => {
      const { data, error } = await supabase.from("events").select("*").eq("is_approved", true);

      if (error) {
        console.error("AdminCalendar: failed to fetch events:", error.message);
        setEvents([]);
        return;
      }
      setEvents(data || []);
    };

    fetchEvents();
  }, []);
  //organizing the events by date 
  const eventsByDateKey = {};
  events.forEach((evt) => {
    const key = evt.date;
    if (!eventsByDateKey[key]) eventsByDateKey[key] = [];
    eventsByDateKey[key].push(evt);
  });

  //hover logic 57-80
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(null);
  const containerRef = useRef(null);

 
  const handleEventMouseEnter = (eventData, e) => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    const targetRect = e.currentTarget.getBoundingClientRect();
    if (!containerRect) return;

    const top = targetRect.bottom - containerRect.top + 4;
    const left = targetRect.left - containerRect.left;

    setHoveredEvent(eventData);
    setHoverPosition({ top, left });
  };

  //mouse hover leave 
  const handleEventMouseLeave = () => {
    setHoveredEvent(null);
    setHoverPosition(null);
  };

  //right side pop up logic 82-100
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [bookmarkedIds, setBookmarkedIds] = useState([]);

  const handleEventClick = (eventData) => {
    setSelectedEvent(eventData);
  };

  const closeDetailPanel = () => {
    setSelectedEvent(null);
  };

  const toggleBookmark = (eventId) => {
    setBookmarkedIds((prev) =>
      prev.includes(eventId)
        ? prev.filter((id) => id !== eventId)
        : [...prev, eventId]
    );
  };

  const isSelectedBookmarked =
    selectedEvent && bookmarkedIds.includes(selectedEvent.id);

  //switch beetween monthss
  const goToPrev = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      if (view === "month") {
        d.setMonth(d.getMonth() - 1);
      } else if (view === "week") {
        d.setDate(d.getDate() - 7);
      } else {
        d.setDate(d.getDate() - 1);
      }
      return d;
    });
  };

  const goToNext = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      if (view === "month") {
        d.setMonth(d.getMonth() + 1);
      } else if (view === "week") {
        d.setDate(d.getDate() + 7);
      } else {
        d.setDate(d.getDate() + 1);
      }
      return d;
    });
  };

  //Month Veiw 
  const renderMonthView = () => {
    const cells = [];

    // Add blank cells so day 1 lands on the correct weekday column
    const firstDayOfWeek = new Date(currentYear, currentMonth, 1).getDay();
    for (let i = 0; i < firstDayOfWeek; i++) {
      cells.push(<div key={`blank-${i}`} className="calendar-day calendar-day--empty" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateObj = new Date(currentYear, currentMonth, day);
      const key = formatDateKey(dateObj);
      const todaysEvents = eventsByDateKey[key] || [];

      cells.push(
        <div key={day} className="calendar-day">
          <div className="day-number">{day}</div>

          <div>
            {todaysEvents.slice(0, 3).map((event, idx) => (
              <div
                key={idx}
                className="event-dot"
                onMouseEnter={(e) => handleEventMouseEnter(event, e)}
                onMouseLeave={handleEventMouseLeave}
                onClick={() => handleEventClick(event)}
              >
                <span className="dot"></span>
                <span className="event-title">{event.name}</span>
              </div>
            ))}

            {todaysEvents.length > 3 && (
              <div className="more-text">
                + {todaysEvents.length - 3} more
              </div>
            )}
          </div>
        </div>
      );
    }

    return cells;
  };

  // WEEK VIEW 
  const renderWeekView = () => {
    const cells = [];

    const startOfWeek = new Date(currentDate);
    const dayOfWeek = startOfWeek.getDay(); // Sunday is 0
    startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);

    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(startOfWeek);
      dayDate.setDate(startOfWeek.getDate() + i);

      const key = formatDateKey(dayDate);
      const todaysEvents = eventsByDateKey[key] || [];

      cells.push(
        <div key={key} className="calendar-day">
          <div className="day-number">
            {dayDate.getMonth() + 1}/{dayDate.getDate()}
          </div>

          <div>
            {todaysEvents.slice(0, 4).map((event, idx) => (
              <div
                key={idx}
                className="event-dot"
                onMouseEnter={(e) => handleEventMouseEnter(event, e)}
                onMouseLeave={handleEventMouseLeave}
                onClick={() => handleEventClick(event)}
              >
                <span className="dot"></span>
                <span className="event-title">{event.name}</span>
              </div>
            ))}

            {todaysEvents.length > 4 && (
              <div className="more-text">
                + {todaysEvents.length - 4} more
              </div>
            )}
          </div>
        </div>
      );
    }

    return cells;
  };

  // DAY VIEW 
  const renderDayView = () => {
    const key = formatDateKey(currentDate);
    const todaysEvents = eventsByDateKey[key] || [];

    return (
      <div className="calendar-day">
        <div className="day-number day-view-label">
          {monthNames[currentMonth]} {currentDate.getDate()}, {currentYear}
        </div>

        <div className="day-view-events">
          {todaysEvents.length === 0 && (
            <div className="no-events-text">No events for this day.</div>
          )}

          {todaysEvents.map((event) => (
            <div
              key={event.id}
              className="day-view-event-card"
              onClick={() => handleEventClick(event)}
            >
              <div className="day-view-event-title">{event.name}</div>

              {event.address && (
                <div className="day-view-event-line">{event.address}</div>
              )}

              {(event.startTime || event.endTime) && (
                <div className="day-view-event-line">
                  {event.startTime} {event.endTime && `- ${event.endTime}`}
                </div>
              )}

              {event.description && (
                <div className="day-view-event-desc">{event.description}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  //decide month/week/day render
  let gridContent;
  if (view === "month") {
    gridContent = renderMonthView();
  } else if (view === "week") {
    gridContent = renderWeekView();
  } else {
    gridContent = renderDayView();
  }

  //set the lables for views 
  const viewLabel = view.charAt(0).toUpperCase() + view.slice(1);

  return (
    <div className="home-container">
    <div className="home-card">
    <div className="calendar-container" ref={containerRef}>
      {/* header */}
          <div className="calendar-top-nav">
              <div className="nav-left">
                  <button className="nav-tab" onClick={() => onViewSelect('admin')}>
                      General
                  </button>
                  <button className="nav-tab active" onClick={() => onViewSelect('adminCalendar')}>
                      Event Calendar
                  </button>
                  <button className="nav-tab" onClick={() => onViewSelect('adminKPIDash')}>
                    KPI Dashboard
                  </button>
                  <button className="nav-tab" onClick={() => onViewSelect('adminEventManagement')}>Event Management</button>
                  <button className="nav-tab" onClick={() => onViewSelect('user-management')}>
                    User Management
                  </button>
                  <button className="nav-tab">
                    Business Management
                  </button>
                  <button className="bpActionBtn secondary" onClick={() => {
                    sessionStorage.removeItem("currentUser");
                    sessionStorage.removeItem("currentView");
                    onViewSelect("loginSignup");
                  }}>
                    Log Out
                  </button>
              </div>

          </div>

      {/* Search bar */}
      <div className="calendar-search-row">
        <div className="search-box">
          <Search className="icon-search" />
          <input placeholder="Search for Event" />
        </div>
      </div>

      {/* Month / Week / Day bar */}
      <div className="calendar-month-row">
        <div className="month-nav">
          <button className="chevron-button" onClick={goToPrev}>
            &lt;
          </button>

          <span className="month-label">
            {monthNames[currentMonth]} {currentYear}
          </span>

          <button className="chevron-button" onClick={goToNext}>
            &gt;
          </button>
        </div>

        <div className="month-actions">

          {/* Add Event button */}
          <button
            className="view-button"
            onClick={() => onAddEvent()}
          >
            + Add Event
          </button>
          <div className="view-dropdown">
            <button
              className="view-button"
              onClick={() => setIsViewMenuOpen((prev) => !prev)}
            >
              {viewLabel}
              <span className="view-caret">▾</span>
            </button>

            {isViewMenuOpen && (
              <div className="view-menu">
                {["month", "week", "day"].map((option) => (
                  <div
                    key={option}
                    className={
                      "view-menu-item" + (view === option ? " active" : "")
                    }
                    onClick={() => {
                      setView(option);
                      setIsViewMenuOpen(false);
                    }}
                  >
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button className="filter-button">
            Filters <Filter size={16} />
          </button>
        </div>
      </div>

      {/* weekday header row */}
      {(view === "month" || view === "week") && (
        <div className="calendar-weekdays">
          {weekdayNames.map((day) => (
            <div key={day} className="weekday-cell">
              {day}
            </div>
          ))}
        </div>
      )}

      {/* Calendar grid */}
      <div className={`calendar-grid ${view}-view`}>{gridContent}</div>

      {/* HOVER POPUP */}
      {hoveredEvent && hoverPosition && !selectedEvent && (
        <div
          className="event-hover-card"
          style={{ top: hoverPosition.top, left: hoverPosition.left }}
        >
          {hoveredEvent.image_url
            ? <img src={hoveredEvent.image_url} alt={hoveredEvent.name} className="hover-event-image" />
            : <div className="hover-image-placeholder">No Image</div>
          }
          <div className="hover-event-body">
            <div className="hover-event-title">{hoveredEvent.name}</div>
            <div className="hover-event-line">
              <span className="hover-label">Date: </span>
              {formatDisplayDate(hoveredEvent.date)}
            </div>
            {(hoveredEvent.startTime || hoveredEvent.endTime) && (
              <div className="hover-event-line">
                <span className="hover-label">Time: </span>
                {hoveredEvent.startTime}
                {hoveredEvent.endTime && ` - ${hoveredEvent.endTime}`}
              </div>
            )}
            {hoveredEvent.address && (
              <div className="hover-event-line">
                <span className="hover-label">Location: </span>
                {hoveredEvent.address}
              </div>
            )}
            {hoveredEvent.description && (
              <div className="hover-event-desc">
                {hoveredEvent.description}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SIDE PANEL */}
      {selectedEvent && (
        <>
          <div
            className="event-detail-backdrop"
            onClick={closeDetailPanel}
          ></div>

          <div className="event-detail-panel">
            <div className="detail-image-wrapper">
              <button className="detail-close-button" onClick={closeDetailPanel}>
                ×
              </button>
              {selectedEvent.image_url
                ? <img src={selectedEvent.image_url} alt={selectedEvent.name} className="detail-event-image" />
                : <div className="detail-image-placeholder">No Image</div>
              }
            </div>

            <div className="event-detail-content">
              <h2 className="detail-title">{selectedEvent.name}</h2>

              <div className="detail-line">
                <span className="detail-label">Date: </span>
                {formatDisplayDate(selectedEvent.date)}
              </div>

              {(selectedEvent.startTime || selectedEvent.endTime) && (
                <div className="detail-line">
                  <span className="detail-label">Time: </span>
                  {selectedEvent.startTime || "TBA"}
                  {selectedEvent.endTime && ` to ${selectedEvent.endTime}`}
                </div>
              )}

              {selectedEvent.admission && (
                <div className="detail-line">
                  <span className="detail-label">Admission: </span>
                  {selectedEvent.admission}
                </div>
              )}

              {selectedEvent.address && (
                <div className="detail-line">
                  <span className="detail-label">Location: </span>
                  {selectedEvent.address}
                </div>
              )}

              {selectedEvent.description && (
                <p className="detail-description">{selectedEvent.description}</p>
              )}

              {selectedEvent.website && (
                <p className="detail-website">
                  <span className="detail-label">Website: </span>
                  <a
                    href={selectedEvent.website}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {selectedEvent.website}
                  </a>
                </p>
              )}
            </div>

            <div className="event-detail-footer">
              <button
                className="edit-toggle"
                onClick={() => {
                  closeDetailPanel();
                  onViewSelect("adminEventManagement", { editEvent: selectedEvent });
                }}
              >
                ✎ Edit
              </button>
            </div>
          </div>
        </>
      )}
    </div>
    </div>
    </div>
  );
}